import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export interface TrackedProduct {
  id: string;
  name: string;
  target_price?: number;
  current_best_price?: number;
  current_best_retailer?: string;
  retailer_name?: string;
  current_best_url?: string;
  normalized_url?: string;
  redirect_url?: string;
  affiliate_commission_rate?: number;
  image_url?: string;
  description?: string;
  category_l1?: string;
  category_l2?: string;
  category_l3?: string;
  wishlist_id?: string;
  enrichment_status: 'pending' | 'crawling' | 'completed' | 'failed';
  last_checked_at?: string;
  created_at: string;
}

interface CreateProductInput {
  name: string;
  target_price?: number;
  normalized_url?: string;
  wishlist_id?: string;
}

interface UpdateProductInput {
  name?: string;
  target_price?: number;
  wishlist_id?: string | null;
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: TrackedProduct }>(`/products/${id}`);
      return res.data.data;
    },
  });
}

export function useProducts(options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: TrackedProduct[] }>('/products');
      return res.data.data;
    },
    refetchInterval: options?.refetchInterval,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProductInput) => {
      const res = await api.post<{ success: boolean; data: TrackedProduct }>('/products', input);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateProductInput & { id: string }) => {
      const res = await api.put<{ success: boolean; data: TrackedProduct }>(`/products/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}

export function useRefreshProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ success: boolean; data: { job_id: string } }>(`/products/${id}/refresh`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['wishlists'] });
    },
  });
}
